import React, { useState, useRef, useEffect, JSX } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../context/ThemeContext";

type DropdownOption = {
  label: string;
  checked?: boolean;
  render?: JSX.Element;
};

type ButtonProps = {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  dropdownOptions?: DropdownOption[];
  onOptionChange?: (index: number, checked: boolean) => void;
};

type TitleButtonsProps = {
  title: string;
  buttons?: ButtonProps[];
  marginLeft?: string | number;
  buttonMarginRight?: string | number;
  buttonGap?: number | string;
  outerMarginLeft?: string | number;
  outerMarginRight?: string | number;
  fullBleedLine?: boolean;
};

const LAYOUT_SIDE_PADDING = 26;

const TitleButtons: React.FC<TitleButtonsProps> = ({
  title,
  buttons = [],
  marginLeft,
  buttonMarginRight,
  buttonGap = 8,
  outerMarginLeft,
  outerMarginRight,
  fullBleedLine = false,
}) => {
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, departmentColor } = useTheme();
  const [fontSize, setFontSize] = useState("13px");
  const [buttonHeight, setButtonHeight] = useState(34);

  const primaryBtnColor = theme?.primary_button || "#54710f";
  const secondaryBtnColor = theme?.secondary_button || "#6c757d";
  const lineColorFinal = theme?.line || "#124280";

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setFontSize(Math.max(12, Math.min(14, w / 135)) + "px");
      setButtonHeight(w < 1000 ? 32 : w < 1400 ? 34 : 36);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const parseMargin = (val?: string | number): string =>
    val !== undefined ? (typeof val === "number" ? `${val}px` : val) : "0px";

  const outerRight = parseMargin(outerMarginRight);
  const outerLeft = parseMargin(outerMarginLeft);

  const wrapperWidth = fullBleedLine
    ? `calc(100% + ${LAYOUT_SIDE_PADDING * 2}px)`
    : "100%";

  const wrapperLeft = fullBleedLine ? `-${LAYOUT_SIDE_PADDING}px` : "0px";

  return (
    <div
      style={{
        ...styles.wrapper,
        marginLeft: outerLeft,
        marginRight: outerRight,
        position: "relative",
        width: wrapperWidth,
        left: wrapperLeft,
      }}
    >
      <div
        style={{
          ...styles.inner,
          width: "100%",
          marginLeft: marginLeft ?? 0,
          position: "relative",
        }}
      >
        <div style={styles.header}>
          <div
            style={{
              ...styles.headerFullLine,
              backgroundColor: departmentColor,
            }}
          />

          <div style={styles.titleContainer}>
            <span style={styles.titleBadge}>{title}</span>
          </div>

          <div style={{ ...styles.buttons, gap: buttonGap }}>
            {buttons.map((btn, idx) => {
              const isSecondary = btn.variant === "secondary";
              const bgColor = isSecondary ? secondaryBtnColor : primaryBtnColor;

              return (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    marginRight: buttonMarginRight ?? 0,
                    zIndex: 2,
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      btn.dropdownOptions
                        ? setOpenDropdown(openDropdown === idx ? null : idx)
                        : btn.onClick()
                    }
                    style={{
                      ...styles.button,
                      height: buttonHeight,
                      fontSize,
                      backgroundColor: bgColor,
                      boxShadow: isSecondary
                        ? "0 4px 12px rgba(80, 80, 80, 0.10)"
                        : "0 5px 14px rgba(18, 66, 128, 0.12)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.filter = "brightness(1.015)";
                      e.currentTarget.style.boxShadow = isSecondary
                        ? "0 8px 18px rgba(80, 80, 80, 0.14)"
                        : "0 9px 20px rgba(18, 66, 128, 0.16)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.filter = "none";
                      e.currentTarget.style.boxShadow = isSecondary
                        ? "0 4px 12px rgba(80, 80, 80, 0.10)"
                        : "0 5px 14px rgba(18, 66, 128, 0.12)";
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.transform = "translateY(1px)";
                      e.currentTarget.style.filter = "brightness(0.985)";
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.filter = "brightness(1.015)";
                    }}
                  >
                    {btn.label}
                  </button>

                  {btn.dropdownOptions && openDropdown === idx && (
                    <div
                      ref={dropdownRef}
                      style={{
                        ...styles.dropdown,
                        top: buttonHeight + 10,
                        border: `1px solid ${lineColorFinal}`,
                      }}
                    >
                      <div
                        style={{
                          maxHeight: 300,
                          overflowY: "auto",
                        }}
                      >
                        {btn.dropdownOptions.map((opt, optIdx) => (
                          <label key={optIdx} style={styles.option}>
                            <input
                              type="checkbox"
                              checked={!!opt.checked}
                              onChange={(e) =>
                                btn.onOptionChange?.(optIdx, e.target.checked)
                              }
                            />
                            <span>{opt.label}</span>
                            {opt.render && (
                              <span style={{ marginLeft: "auto" }}>
                                {opt.render}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setOpenDropdown(null);
                          btn.onClick();
                        }}
                        style={{
                          ...styles.button,
                          ...styles.confirmButton,
                          height: buttonHeight,
                          fontSize,
                          backgroundColor: lineColorFinal,
                          boxShadow: "0 5px 14px rgba(18, 66, 128, 0.12)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.filter = "brightness(1.015)";
                          e.currentTarget.style.boxShadow =
                            "0 9px 20px rgba(18, 66, 128, 0.16)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.filter = "none";
                          e.currentTarget.style.boxShadow =
                            "0 5px 14px rgba(18, 66, 128, 0.12)";
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.transform = "translateY(1px)";
                          e.currentTarget.style.filter = "brightness(0.985)";
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.filter = "brightness(1.015)";
                        }}
                      >
                        Gerar PDF
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: {
    marginTop: 20,
  },

  inner: {
    position: "relative",
  },

  header: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 20,
    width: "100%",
    minHeight: 56,
  },

  headerFullLine: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    width: "100%",
    height: 2,
    zIndex: 0,
  },

  titleContainer: {
    position: "relative",
    zIndex: 2,
    paddingLeft: 4,
    paddingTop: 4,
    display: "flex",
    alignItems: "flex-end",
    alignSelf: "stretch",
  },

  titleBadge: {
    color: "#545454",
    fontFamily: "Poppins",
    fontWeight: 500,
    fontSize: 14,
    padding: "8px 15px",
    marginTop: 14,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 20,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    minHeight: 32,
  },

  buttons: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    alignSelf: "flex-end",
    paddingBottom: 5,
    paddingRight: 14,
    marginLeft: 24,
  },

  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: "Poppins, Inter, sans-serif",
    fontWeight: 400,
    cursor: "pointer",
    userSelect: "none",
    padding: "0 16px",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 10,
    outline: "none",
    letterSpacing: "0.005em",
    whiteSpace: "nowrap",
    transition:
      "transform 160ms ease, box-shadow 200ms ease, filter 200ms ease, background-color 200ms ease, border-color 200ms ease",
    WebkitTapHighlightColor: "transparent",
  },

  dropdown: {
    position: "absolute",
    right: 0,
    background: "#fff",
    borderRadius: 12,
    padding: 10,
    boxShadow: "0 12px 28px rgba(0,0,0,0.14)",
    zIndex: 20,
    width: 260,
  },

  option: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
    color: "#333",
    cursor: "pointer",
    padding: "6px 4px",
  },

  confirmButton: {
    marginTop: 10,
    width: "100%",
  },
};

export default TitleButtons;
